/**
 * 営業日パターンマスタを適用した日数計算
 * - 最短お預かり日の算出
 * - 曜日から加算リードタイムの算出
 */

import { ModelYhcRegularWorkingDays } from '../../fireModels/yhcDataTypes'
import { judgementFixedHoliday } from '../fixedHolidaysJudgeFunction'

const JPN_WEEKDAYS_ORDER = ['日', '月', '火', '水', '木', '金', '土']
type Weekday = (typeof JPN_WEEKDAYS_ORDER)[number]

/**
 * Excel 取込用: 加算日数セルを DB 保存値に正規化する
 * - 空・0・非数値・小数・負数 → null
 * - 正の整数 → その値
 */
export const normalizeAdditionalLeadTimeDaysForImport = (
  cell: unknown,
): number | null => {
  if (cell === null || cell === undefined) {
    return null
  }
  if (typeof cell === 'string' && cell.trim() === '') {
    return null
  }
  const n = Number(cell)
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
    return null
  }
  return n
}

/**
 * 郵便番号マスタの加算日数をリードタイム計算用の日数に変換する
 * - null・0・空・非数値・負数 → 0（加算なし）
 * - 正の整数 → その値
 */
export const parseAdditionalLeadTimeDays = (value: unknown): number => {
  if (value === null || value === undefined) {
    return 0
  }
  if (typeof value === 'string' && value.trim() === '') {
    return 0
  }
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
    return 0
  }
  return n
}

/**
 * 最短お預かり日の算出
 * @param pickupDate
 * @param regularWorkingDay
 */
export const calcEarliestPickupDate = (
  pickupDate: string,
  fromRegularWorkingDay: ModelYhcRegularWorkingDays,
): string => {
  if (!fromRegularWorkingDay || fromRegularWorkingDay.regular_weekdays?.length === 7) {
    return pickupDate // 営業日パターンマスタが見つからないとか営業曜日がすべてのときはそのまま返す
  }
  const regular_weekdays = fromRegularWorkingDay.regular_weekdays || []
  // 営業日が一つも設定されていない場合はエラーを投げる
  if (regular_weekdays.length === 0) {
    throw new Error('営業日が設定されていません。')
  }

  // 1. pickupDateをdayjsオブジェクトに変換
  let currentDate = $core.$dayjs(pickupDate)

  // 2. 曜日をチェックし、営業日ならそのまま返す
  // format('dd')で日本語の曜日を取得します (例: "月")
  const dayOfWeek = currentDate.format('dd')
  if (regular_weekdays.includes(dayOfWeek)) {
    return pickupDate
  }

  // 3. 営業日でない場合、次の営業日が見つかるまで1日ずつ進める
  // 営業日は必ず1週間以内にあるため、ループは最大7回で終了します
  for (let i = 0; i < 7; i++) {
    // dayjsはイミュータブル(不変)なので、add()メソッドは新しいdayjsオブジェクトを返します
    currentDate = currentDate.add(1, 'day')
    const nextDayOfWeek = currentDate.format('dd')

    if (regular_weekdays.includes(nextDayOfWeek)) {
      // 見つかった営業日の日付を "YYYY-MM-DD" 形式で返す
      return currentDate.format('YYYY-MM-DD')
    }
  }

  // この行は、上記のロジックでは到達不能（unreachable）ですが、
  // 万が一のケースとTypeScriptの型チェックのために記述します。
  throw new Error('予期せぬエラー：営業日が見つかりませんでした。')
}

/**
 * お預かり日またはお届け日が定休日だった場合、次の営業日がお預かり日、お届け日となるよう加算リードタイムを算出する
 */
export const addingLeadTimeDaysByRegularWorkingDays = (
  pickUpOrdeliveryDate: string,
  regularWorkingDay: ModelYhcRegularWorkingDays,
): number => {
  if (!regularWorkingDay || regularWorkingDay.regular_weekdays?.length === 7) {
    // 営業日パターンマスタが見つからないまたは営業曜日がすべてのときは0日を返す
    return 0
  }

  const regular_weekdays = regularWorkingDay.regular_weekdays || []
  // お届け日をdayjsオブジェクトに変換
  let currentDate = $core.$dayjs(pickUpOrdeliveryDate)

  // お届け日が営業日であるかチェック
  if (regular_weekdays.includes(currentDate.format('dd'))) {
    return 0 // 営業日ならリードタイムは0日
  }

  // 営業日でない場合、次の営業日までの日数を計算
  for (let i = 1; i <= 7; i++) {
    currentDate = currentDate.add(1, 'day')
    if (regular_weekdays.includes(currentDate.format('dd'))) {
      return i // 次の営業日までの日数を返す
    }
  }

  // この行は到達不能ですが、万が一のケースとして記述します。
  throw new Error('予期せぬエラー：次の営業日が見つかりませんでした。')
}

/**
 * お届け日の曜日が定休日だった場合、次の営業曜日がお届け日となるよう加算リードタイムを算出する
 * @param dayOfWeek 基準となる曜日 (例: "月", "火")
 * @param toRegularWorkingDay 営業日情報を持つオブジェクト
 * @returns 加算する日数 (リードタイム)
 */
export const addingLeadTimeDaysForDeliveryByDayOfWeek = (
  dayOfWeek: string,
  toRegularWorkingDay: ModelYhcRegularWorkingDays,
): number => {
  const regular_weekdays = toRegularWorkingDay?.regular_weekdays

  // ガード節1: 営業日パターンが未定義、または全曜日(7日)が営業日の場合は加算不要
  if (!regular_weekdays || regular_weekdays.length >= 7) {
    return 0
  }
  // ガード節2: 営業日が一つも設定されていない場合は、加算しようがないので0を返す
  if (regular_weekdays.length === 0) {
    return 0
  }

  // チェック1: お届け予定日の曜日が営業日であれば、加算リードタイムは0日
  if (regular_weekdays.includes(dayOfWeek)) {
    return 0
  }

  // チェック2: 営業日でない場合、次の営業日までの日数を計算

  // 基準となる曜日のインデックスを取得 (0:日, 1:月, ...)
  const startIndex = JPN_WEEKDAYS_ORDER.indexOf(dayOfWeek as Weekday)

  // ガード節3: 入力された曜日文字列が不正な場合 (例: "火曜")
  if (startIndex === -1) {
    console.error(`不正な曜日が指定されました: ${dayOfWeek}`)
    // 不正な入力に対してはエラーを投げるか、0を返すかは仕様によります。
    // ここでは安全のため0を返します。
    return 0
  }

  // 基準の曜日の翌日(+1日後)から最大7日間チェックする
  for (let i = 1; i <= 7; i++) {
    // (startIndex + i) % 7 で、次の曜日のインデックスを循環的に計算する
    // 例: startIndexが6(土)でiが1なら、(6+1)%7 = 0となり、次の曜日は日曜日と正しく計算される
    const nextDayIndex = (startIndex + i) % 7
    const nextDayOfWeek = JPN_WEEKDAYS_ORDER[nextDayIndex]

    if (regular_weekdays.includes(nextDayOfWeek)) {
      // 次の営業日が見つかったら、そこまでの日数(i)をリードタイムとして返す
      return i
    }
  }

  // このコードパスは、regular_weekdaysが空でない限り到達不能(unreachable)です。
  // ガード節でチェック済みですが、TypeScriptの型チェックと万が一のケースのために残します。
  return 0
}

/**
 * 年末年始休業日を考慮した最短お預かり日の算出
 * @param pickupDate 基準となるお預かり日
 * @param fromRegularWorkingDay 営業日パターンマスタ
 * @returns 年末年始休業日と営業日パターンを考慮した最短お預かり日
 */
export const calcEarliestPickupDateWithFixedHolidays = async (
  pickupDate: string,
  fromRegularWorkingDay: ModelYhcRegularWorkingDays | null,
): Promise<string> => {
  let candidateDate = calcEarliestPickupDate(pickupDate, fromRegularWorkingDay)
  let currentDate = $core.$dayjs(candidateDate)
  const regularWeekdays = fromRegularWorkingDay?.regular_weekdays || []
  const hasRegularPattern = regularWeekdays.length > 0 && regularWeekdays.length < 7

  for (let i = 0; i < 30; i++) {
    const currentDateObj = currentDate.toDate()
    const isHoliday = await judgementFixedHoliday(currentDateObj)

    if (!isHoliday) {
      if (!hasRegularPattern) {
        // 営業日パターンがない場合はそのまま返す
        return currentDate.format('YYYY-MM-DD')
      }

      const dayOfWeek = currentDate.format('dd')
      if (regularWeekdays.includes(dayOfWeek)) {
        return currentDate.format('YYYY-MM-DD')
      }
    }

    currentDate = currentDate.add(1, 'day')
  }

  return candidateDate
}

/**
 * 年末年始休業日を考慮した日数加算
 * @param baseDate 基準日
 * @param needDays 加算する日数
 * @param regularWorkingDay 営業日パターンマスタ
 * @returns 年末年始休業日と営業日パターンを考慮した日付
 */
export const addingLeadTimeDaysByRegularWorkingDaysWithFixedHolidays = async (
  baseDate: string,
  needDays: number,
  regularWorkingDay: ModelYhcRegularWorkingDays | null,
): Promise<string> => {
  if (!regularWorkingDay || regularWorkingDay.regular_weekdays?.length === 7) {
    // 営業日パターンマスタが見つからないまたは営業曜日がすべてのとき
    let currentDate = $core.$dayjs(baseDate).add(needDays, 'day')

    for (let i = 0; i < 30; i++) {
      const currentDateObj = currentDate.toDate()
      const isHoliday = await judgementFixedHoliday(currentDateObj)

      if (!isHoliday) {
        return currentDate.format('YYYY-MM-DD')
      }
      currentDate = currentDate.add(1, 'day')
    }

    return currentDate.format('YYYY-MM-DD')
  }

  const regular_weekdays = regularWorkingDay.regular_weekdays || []
  let currentDate = $core.$dayjs(baseDate)
  let addedDays = 0

  const maxIterations = needDays * 3 + 30
  for (let i = 0; i < maxIterations; i++) {
    currentDate = currentDate.add(1, 'day')
    const dayOfWeek = currentDate.format('dd')
    const currentDateObj = currentDate.toDate()
    const isHoliday = await judgementFixedHoliday(currentDateObj)

    if (regular_weekdays.includes(dayOfWeek) && !isHoliday) {
      addedDays++
      if (addedDays >= needDays) {
        return currentDate.format('YYYY-MM-DD')
      }
    }
  }

  throw new Error('予期せぬエラー：営業日が見つかりませんでした。')
}

/**
 * お届け日が年末年始休業日の場合、休業明けの営業日に調整する
 * @param deliveryDate 基準となるお届け日
 * @returns 年末年始休業日を考慮したお届け日
 */
export const adjustDeliveryDateForFixedHolidays = async (
  deliveryDate: string,
): Promise<string> => {
  let currentDate = $core.$dayjs(deliveryDate)

  for (let i = 0; i < 30; i++) {
    const currentDateObj = currentDate.toDate()
    const isHoliday = await judgementFixedHoliday(currentDateObj)

    if (!isHoliday) {
      return currentDate.format('YYYY-MM-DD')
    }

    currentDate = currentDate.add(1, 'day')
  }

  // 30日先まで営業日が見つからない場合はそのまま返す
  return deliveryDate
}

/**
 * お届け日が年末年始休業日または定休日の場合、次の営業日に調整する
 * @param deliveryDate 基準となるお届け日
 * @param toRegularWorkingDay お届け先の営業日パターンマスタ
 * @returns 年末年始休業日と定休日を考慮したお届け日
 */
export const adjustDeliveryDateForFixedHolidaysAndRegularWorkingDays = async (
  deliveryDate: string,
  toRegularWorkingDay: ModelYhcRegularWorkingDays | null,
): Promise<string> => {
  let currentDate = $core.$dayjs(deliveryDate)
  const regularWeekdays = toRegularWorkingDay?.regular_weekdays || []
  const hasRegularPattern = regularWeekdays.length > 0 && regularWeekdays.length < 7

  for (let i = 0; i < 30; i++) {
    const currentDateObj = currentDate.toDate()
    const isFixedHoliday = await judgementFixedHoliday(currentDateObj)

    if (!isFixedHoliday) {
      if (!hasRegularPattern) {
        return currentDate.format('YYYY-MM-DD')
      }

      const dayOfWeek = currentDate.format('dd')
      if (regularWeekdays.includes(dayOfWeek)) {
        return currentDate.format('YYYY-MM-DD')
      }
    }

    currentDate = currentDate.add(1, 'day')
  }

  return deliveryDate
}
